// ═══════════════════════════════════════════════════════════════════════════════
// FILE: src/app/guards/cal-status.guard.ts
// VERSION: v3.0.0 – Session/CAL Layer Separation + SessionHealth Heartbeat
// DATE: 2026-05-03
//
// CHANGES v3.0.0:
//   ✅ FIX: 401 (Session) → /login (nicht /no-license!)
//   ✅ FIX: 402 (no CAL) → /no-license (korrekt)
//   ✅ NEW: Startet SessionHealthService Heartbeat nach erfolgreichem Check
//   ✅ KEPT: Super-Admin Bypass (orgAccessPermission >= 7)
//
// ROUTING RULES:
//   401 → /login (Session weg → Login nötig)
//   402 → /no-license (User ist authenticated, hat aber keine CAL)
//   Valid → route erlaubt + Heartbeat gestartet
// ═══════════════════════════════════════════════════════════════════════════════

import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { CalStatusService } from '../services/cal-status.service';
import { SessionHealthService } from '../services/session-health.service';

export const calStatusGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const calStatusService = inject(CalStatusService);
  const sessionHealth = inject(SessionHealthService);
  const router = inject(Router);

  console.log('[CalStatusGuard] v3.0.0 Checking for route:', state.url);

  try {
    const result = await calStatusService.checkCalStatusSilent();

    // ── Super-Admin Bypass (perm >= 7) ───────────────────────────────────
    if (result.orgAccessPermission >= 7) {
      console.log('[CalStatusGuard] ✅ Super-Admin bypass (perm=%d)', result.orgAccessPermission);
      calStatusService.startPeriodicRefresh();
      sessionHealth.startHeartbeat();  // ← NEU v3.0.0
      return true;
    }

    // ── Valid CAL → allow ────────────────────────────────────────────────
    if (result.calStatus.hasLicense && result.calStatus.status === 'active') {
      console.log('[CalStatusGuard] ✅ Access GRANTED - Valid CAL');
      calStatusService.startPeriodicRefresh();
      sessionHealth.startHeartbeat();  // ← NEU v3.0.0
      return true;
    }

    // ── No valid CAL → /no-license (NOT /login!) ────────────────────────
    console.warn('[CalStatusGuard] ❌ No valid CAL → /no-license');
    return router.createUrlTree(['/no-license']);

  } catch (error: any) {
    // ─────────────────────────────────────────────────────────────────────
    // v3.0.0 FIX: 401 → /login (Session-Problem, NICHT CAL-Problem)
    // ─────────────────────────────────────────────────────────────────────
    if (error instanceof HttpErrorResponse && error.status === 401) {
      console.warn('[CalStatusGuard] 🔐 Session expired (401) → /login');
      // SessionHealthService.handleSessionExpired() wurde bereits im
      // CalStatusService aufgerufen → kein Doppel-Handling nötig.
      // Falls es doch nicht gecalled wurde (race condition), hier sicherstellen:
      if (!sessionHealth.isHandlingExpiry) {
        sessionHealth.handleSessionExpired(error, 'cal-status-guard');
      }
      // Nicht nach /no-license, sondern zum Login!
      return router.createUrlTree(['/login']);
    }

    console.error('[CalStatusGuard] Error checking CAL:', error);
    return router.createUrlTree(['/no-license']);
  }
};
